import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import InputWrapper from "../../layout/inputWrapper";
import ButtonWrapper from "../../components/Button";
import { Col, Container, Row } from "react-bootstrap";
import { yesOrNo } from "../../../TCPForms/data";
import { Box, Modal, Typography } from "@mui/material";

const Step3 = ({ formData, handleTabChange, isEmployee }) => {
  const { t } = useTranslation();
  const { documents } = useSelector((state) => state.formMT);
  const [showError, setShowError] = useState(false);
  const [exceedHeight, setExceedHt] = useState(false);
  const [zeroHeightError, setZeroHeightError] = useState(false);
  const [open, setOpen] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");

  const defaultValues = {
    ...formData,
    descriptionOfLand: {
      ...formData?.descriptionOfLand,
    },
  };
  const { handleSubmit, control, setValue, watch, reset, getValues, setError } = useForm({ defaultValues });

  const buildingHeight = watch("planheightOfBuilding");
  const [watchPlanLocation, watchPlandimensionOfPlot, watchPlanfloorPlans, watchFloors] = watch([
    "planlocationOfPlot",
    "plandimensionOfPlot",
    "planfloorPlans",
    "containMoreThanThree",
  ]);

  useEffect(() => {
    // Check for zero height
    if (buildingHeight === "0" || buildingHeight === "0.00") {
      setZeroHeightError(true);
      setOpen(true);
      setValidationMessage("0.00 is not allowed");
      return;
    } else {
      setZeroHeightError(false);
    }

    // Check for height exceeding 15m
    if (buildingHeight > 15) {
      setExceedHt(true);
      setOpen(true);
      setValidationMessage("Building height should not exceed 15m.");
    } else {
      setExceedHt(false);
    }
  }, [buildingHeight]);

  useEffect(() => {
    // Check if any radio button is set to 'no'
    const hasNoSelection = watchPlanLocation === "no" || watchPlandimensionOfPlot === "no" || watchPlanfloorPlans === "no";
    const hasFloors = watchFloors === "yes";
    if (hasNoSelection) {
      setOpen(true);
      setValidationMessage("Please show it in the attached Plan");
    }
    if (hasFloors) {
      setOpen(true);
      setValidationMessage("Please submit your proposal through normal Map Submission process");
    }
  }, [watchPlanLocation, watchPlandimensionOfPlot, watchPlanfloorPlans, watchFloors]);

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    setValue("documents", documents);
  }, [documents]);

  const onSubmit = (data) => {
    handleTabChange(2, data);
  };

  // Function to check if field should be disabled
  const shouldDisableField = (fieldName) => {
    // If height exceeds 15m, disable all fields except height
    if (exceedHeight) {
      return fieldName !== "planheightOfBuilding";
    }
    // If any radio is 'no', disable all fields except the radio buttons
    if (watchPlanLocation === "no") {
      return !["planlocationOfPlot"].includes(fieldName);
    } else if (watchPlandimensionOfPlot === "no") {
      return !["plandimensionOfPlot"].includes(fieldName);
    } else if (watchPlanfloorPlans === "no") {
      return !["planfloorPlans"].includes(fieldName);
    } else if (watchFloors === "yes") {
      return !["containMoreThanThree"].includes(fieldName);
    }
    return false;
  };

  return (
    <section className="groupContainer2">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="formContainer">
          <div className="formHeaderContainer">
            <h2 className="formHeading">{"Details of Map"}</h2>
          </div>
          <Container fluid className="formInputContainer common-app-form">
            <h2 className="formHeading" style={{ fontWeight: "bold", marginBottom: "13px" }}>
              {t("Does the attached Plan show?")}
            </h2>
            <Row>
              <Col>
                <InputWrapper
                  control={control}
                  label={"Location of plot along with existing roads and their width "}
                  name="planlocationOfPlot"
                  type="radioButton"
                  mandatory
                  data={yesOrNo}
                  disabled={shouldDisableField("planlocationOfPlot") || isEmployee}
                  defaultValue={formData?.planlocationOfPlot}
                  validation={{
                    required: {
                      value: true,
                      message: "This field is required",
                    },
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <InputWrapper
                  control={control}
                  label={"Dimensions of plot/premises along with setback"}
                  name="plandimensionOfPlot"
                  type="radioButton"
                  mandatory
                  data={yesOrNo}
                  disabled={shouldDisableField("plandimensionOfPlot") || isEmployee}
                  defaultValue={formData?.plandimensionOfPlot}
                  validation={{
                    required: {
                      value: true,
                      message: "This field is required",
                    },
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <InputWrapper
                  control={control}
                  label={
                    "Include floor plans of all floors together with the covered area clearly indicating sizes of rooms and the position and width of staircases and other exits"
                  }
                  name="planfloorPlans"
                  type="radioButton"
                  mandatory
                  height={55}
                  disabled={shouldDisableField("planfloorPlans") || isEmployee}
                  data={yesOrNo}
                  defaultValue={formData?.planfloorPlans}
                  validation={{
                    required: {
                      value: true,
                      message: "This field is required",
                    },
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <InputWrapper
                  control={control}
                  label={"Is your proposal contain more than 3 floors? "}
                  name="containMoreThanThree"
                  type="radioButton"
                  mandatory
                  data={yesOrNo}
                  disabled={shouldDisableField("containMoreThanThree") || isEmployee}
                  defaultValue={formData?.plandimensionOfPlot}
                  validation={{
                    required: {
                      value: true,
                      message: "This field is required",
                    },
                  }}
                />
              </Col>
            </Row>
            <h2 className="formHeading" style={{ fontWeight: "bold", marginTop: "10px" }}>
              Parking Details & Building Height:
            </h2>
            <Row>
              <Col>
                <InputWrapper
                  control={control}
                  label={"Height of Building (mtr)"}
                  name="planheightOfBuilding"
                  type="textInput"
                  mandatory
                  disabled={shouldDisableField("planheightOfBuilding") || isEmployee}
                  defaultValue={formData?.planheightOfBuilding}
                  placeholder={"Enter Height of Building"}
                  validation={{
                    required: {
                      value: true,
                      message: "This field is required",
                    },
                    min: {
                      value: 0.01,
                      message: "Height must be greater than 0",
                    },
                    pattern: {
                      value: /^\d+(\.\d{1,2})?$/,
                      message: "Only up to 2 decimal places allowed",
                    },
                  }}
                />
              </Col>
            </Row>
            {formData?.plotArea > 50 && (
              <Row>
                <Col>
                  <InputWrapper
                    control={control}
                    label={"Area of parking spaces provided (If plot size is more than 50 sqm) (in sqm)"}
                    name="plansParkingSpace"
                    type="textInput"
                    mandatory
                    defaultValue={formData?.plansParkingSpace}
                    placeholder={"Enter Parking Space"}
                    disabled={shouldDisableField("plansParkingSpace") || isEmployee}
                    validation={{
                      required: {
                        value: true,
                        message: "This field is required",
                      },
                    }}
                  />
                </Col>
              </Row>
            )}
            <Modal open={open} onClose={() => {}} closeAfterTransition aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 400,
                  bgcolor: "background.paper",
                  borderRadius: 2,
                  boxShadow: 24,
                  p: 4,
                  outline: "none",
                }}
              >
                <Typography id="modal-modal-title" variant="h6" component="h2" fontWeight="bold" sx={{ textAlign: "center" }}>
                  Not Permitted
                </Typography>

                <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                  {validationMessage}
                </Typography>

                <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    onClick={handleClose}
                    style={{
                      backgroundColor: "rgb(21, 124, 179)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "8px",
                      padding: "10px 24px",
                      cursor: "pointer",
                      fontSize: "16px",
                      fontWeight: "500",
                      transition: "background-color 0.3s ease",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = "rgb(15, 104, 155)";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = "rgb(21, 124, 179)";
                    }}
                  >
                    OK
                  </button>
                </Box>
              </Box>
            </Modal>
          </Container>
        </div>
        {!isEmployee && (
          <div className="footerContainer">
            <div className="buttonWrapper">
              <ButtonWrapper
                variant="contained"
                color="success"
                size="medium"
                disabled={exceedHeight || zeroHeightError}
                label="Save"
                type="submit"
                sx={{ backgroundColor: "#157CB3" }}
              />
            </div>
          </div>
        )}
      </form>
    </section>
  );
};

export default Step3;
